import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { logout as doLogout, teacherApi } from "@/lib/api";
import { FlaskConical, LogOut, Settings, Shield } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// How often to re-poll the admin health-summary for the notification badge.
// 60s is enough for real-time-ish feedback awareness without hammering the API.
const HEALTH_POLL_MS = 60_000;

export default function TeacherHeader() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [role, setRole] = useState(null);
  const [unreadFeedback, setUnreadFeedback] = useState(0);

  useEffect(() => {
    teacherApi.get("/auth/me").then(({ data }) => setRole(data?.role)).catch(() => {});
  }, []);

  // Poll admin health-summary for unread feedback count — admin only.
  useEffect(() => {
    if (role !== "admin") return;
    let stopped = false;
    const fetchHealth = async () => {
      try {
        const { data } = await teacherApi.get("/admin/health-summary");
        if (!stopped) setUnreadFeedback(data?.feedback?.new ?? 0);
      } catch {
        /* silent — dot just won't update */
      }
    };
    fetchHealth();
    const interval = setInterval(fetchHealth, HEALTH_POLL_MS);
    return () => { stopped = true; clearInterval(interval); };
  }, [role]);

  const logout = async () => {
    await doLogout();
    nav("/teacher/login", { replace: true });
  };

  return (
    <header className="border-b-2 border-[#0F172A] bg-white sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/teacher" className="flex items-center gap-3" data-testid="teacher-home-link">
          <div className="brut bg-[#FEF08A] w-9 h-9 flex items-center justify-center rounded-md">
            <FlaskConical className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-black text-lg leading-none">
              {t("teacher.brand_name")}
            </div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">
              {t("teacher.brand_tagline")}
            </div>
          </div>
        </Link>
        <nav className="flex items-center gap-2">
          <Link to="/teacher">
            <Button variant="ghost" className="font-bold rounded-md" data-testid="nav-sessions">
              {t("teacher.nav.sessions")}
            </Button>
          </Link>
          <Link to="/teacher/questions">
            <Button variant="ghost" className="font-bold rounded-md" data-testid="nav-questions">
              {t("teacher.nav.questions")}
            </Button>
          </Link>
          {role === "admin" && (
            <Link to="/teacher/admin" className="relative">
              <Button variant="ghost" className="font-bold rounded-md text-[#DC2626]" data-testid="nav-admin">
                <Shield className="w-4 h-4 mr-2" strokeWidth={2.5} />{t("teacher.nav.admin")}
              </Button>
              {unreadFeedback > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-[#DC2626] text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center border-2 border-white shadow-[2px_2px_0_#0F172A] animate-pulse"
                  title={`${unreadFeedback} nieprzeczytanych zgłoszeń od beta-testerów`}
                  data-testid="feedback-notification-badge"
                >
                  {unreadFeedback > 9 ? "9+" : unreadFeedback}
                </span>
              )}
            </Link>
          )}
          <Link to="/teacher/settings">
            <Button variant="ghost" className="font-bold rounded-md" data-testid="nav-settings">
              <Settings className="w-4 h-4 mr-2" strokeWidth={2.5} />{t("teacher.nav.settings")}
            </Button>
          </Link>
          <Button
            onClick={logout}
            variant="outline"
            className="brut brut-hover bg-white font-bold rounded-md border-[#0F172A]"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={2.5} />{t("teacher.nav.logout")}
          </Button>
          <LanguageSwitcher />
        </nav>
      </div>
    </header>
  );
}
